import { useQuery } from '@apollo/client'
import { Icon } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Table as BaseTable, SortableHeader } from '@/components_deprecated'
import { CenteredErrorMessage } from '@/components_deprecated/CenteredErrorMessage'
import React, { useCallback } from 'react'
import { isMobile } from 'react-device-detect'
import { Link } from 'react-router'
import styled, { css } from 'styled-components'
import { format, formatDistanceToNow } from '@/utils/date-fns'
import { cleanRegexOperators } from '@/utils/forms'
import { useAdmin, useInfiniteScroll, useOnErrorAuto } from '@/utils/hooks'
import {
  FORUM_DIRECT_THREADS_QUERY,
  FORUM_GENERAL_THREADS_QUERY,
} from '../../queries'
import type {
  ForumThreadsQuery,
  ForumThreadsQueryVariables,
} from '../../types.graphql'
import { SeenBar } from './SeenBar'
import ThreadsCards from './ThreadsCards'
import type { RecipientItem } from '../../types'

const TD_HEIGHT = '72px'

const Table = styled(BaseTable)`
  overflow-x: auto;

  td {
    height: ${TD_HEIGHT} !important;
    font-weight: 500;

    div.post-count {
      display: inline-flex;
      justify-content: center;
      min-width: 42px;
      padding: 8px;
      border-radius: 6px;
      background-color: ${props => props.theme.colors.quaternary};
    }

    &.author {
      display: flex;
      flex-direction: column;
      justify-content: center;

      span {
        font-size: 0.8em;
        color: ${props => props.theme.colors.gray5};
      }
    }
  }
`

const RecipientsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  font-size: 0.9em;

  .recipient-item {
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    &.group {
      font-weight: 600;
      color: ${props => props.theme.colors.primary};
    }
  }

  .more {
    color: ${props => props.theme.colors.gray5};
    font-style: italic;
  }
`

interface TitleProps {
  $sticky: boolean
  $seen: boolean
}

const Title = styled(Link)<TitleProps>`
  position: relative;
  padding-left: 6px;

  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  font-weight: ${props => (props.$seen ? 500 : 800)};

  ${props =>
    props.$sticky &&
    css`
      &::before {
        content: '';
        box-sizing: content-box;
        position: absolute;
        top: 50%;
        left: -12px;
        width: 6px;
        height: calc(${TD_HEIGHT} - 12px);
        transform: translateY(-50%);

        background-color: ${props => props.theme.colors.orange};
        border-radius: 999px;
      }
    `};
`

const FORUM_PAGE_SIZE = 50

interface ThreadsTableProps {
  query?: string
  sort?: string

  isDirectMessage?: boolean
  leaderGroupId?: string | null

  onSort?: (value: string) => void
}

export const ThreadsTable: React.FC<ThreadsTableProps> = ({
  query = '',
  sort = '-createdAt',
  isDirectMessage = false,
  leaderGroupId = null,

  onSort = () => void 0,
}) => {
  const translations = useTranslate({
    title: 'common.title',
    recipient: 'common.recipient',
    author: 'common.author',
    created: 'forum.created',

    countOfTotal: ['common.count-of-total', { count: <em />, total: 0 }],

    errorThreads: 'forum.threads-error-message',
  })

  const admin = useAdmin()
  const onErrorAuto = useOnErrorAuto()
  const queryDocument = isDirectMessage
    ? FORUM_DIRECT_THREADS_QUERY
    : FORUM_GENERAL_THREADS_QUERY

  const { data, loading, error, fetchMore } = useQuery<
    ForumThreadsQuery,
    ForumThreadsQueryVariables
  >(queryDocument, {
    fetchPolicy: 'cache-first',
    variables: {
      first: FORUM_PAGE_SIZE,
      q: cleanRegexOperators(query),
      orderBy: sort,
      withAdminFields: admin,
      ...(leaderGroupId !== null && { leaderGroupId }),
    },
    onError: onErrorAuto(translations.errorThreads),
  })

  const pageInfo = data?.allThreads.pageInfo
  const hasNextPage = pageInfo?.hasNextPage ?? false

  const handleScrollBottom = useCallback(async () => {
    if (typeof data === 'undefined' || typeof pageInfo === 'undefined') return
    if (!hasNextPage || !pageInfo.endCursor) return

    const cursor = pageInfo.endCursor
    try {
      await fetchMore({
        variables: {
          first: FORUM_PAGE_SIZE,
          q: cleanRegexOperators(query),
          orderBy: sort,
          after: cursor,
          withAdminFields: admin,
          ...(leaderGroupId !== null && { leaderGroupId }),
        },
      })
    } catch (e) {
      if (e instanceof Error && e.name === 'Invariant Violation') return
      throw e
    }
  }, [
    fetchMore,
    query,
    sort,
    admin,
    pageInfo,
    data,
    hasNextPage,
    leaderGroupId,
  ])

  useInfiniteScroll(handleScrollBottom, 200, !loading && hasNextPage)

  const formatRecipientsColumn = (recipients: string[]) => {
    if (!recipients || recipients.length === 0) return []

    const groups = recipients.filter(r => r.startsWith('Group:'))
    const users = recipients.filter(r => !r.startsWith('Group:'))

    const result: RecipientItem[] = [
      ...groups.slice(0, 2).map(
        group =>
          ({
            text: group.replace('Group: ', ''),
            isGroup: true,
          }) as const
      ),
      ...users.slice(0, Math.max(0, 3 - Math.min(groups.length, 2))).map(
        user =>
          ({
            text: user,
            isGroup: false,
          }) as const
      ),
    ]

    const remaining = recipients.length - result.length
    if (remaining > 0) {
      result.push({ text: `+${remaining} more`, isMore: true })
    }

    return result
  }
  const formatPostCount = (count: number) => {
    if (count > 99) return '99+'
    return count.toString()
  }
  const allStickies = data?.stickyThreads.edges.map(edge => edge.node) ?? []
  const allThreads = data?.allThreads.edges.map(edge => edge.node) ?? []

  const threads = allStickies.concat(allThreads)

  if (error) return <CenteredErrorMessage />

  if (isMobile) return <ThreadsCards threads={threads} loading={loading} />

  return (
    <Table
      loading={loading}
      loaderProps={{
        columns: admin ? 5 : 4,
      }}
    >
      <thead>
        <tr>
          <SortableHeader baseValue="title" sortValue={sort} onSort={onSort}>
            {translations.title}
          </SortableHeader>

          <th>{translations.author}</th>

          {isDirectMessage && <th>{translations.recipient}</th>}

          <SortableHeader
            baseValue="createdAt"
            sortValue={sort}
            invertDirection
            onSort={onSort}
          >
            {translations.created}
          </SortableHeader>

          <th className="center">
            <Icon icon="comment" size="1.2em" />
          </th>

          {admin && (
            <th className="center">
              <Icon icon="eye" size="1.2em" />
            </th>
          )}
        </tr>
      </thead>

      <tbody>
        {threads.map(thread => {
          const createdAt = new Date(thread.createdAt)
          const formattedCreatedAt = format(createdAt, 'PPPP HH:mm')
          const timeAgo = formatDistanceToNow(createdAt, {
            addSuffix: true,
          })

          return (
            <tr key={thread.id}>
              <td>
                <Title
                  to={`/forum/${thread.id}`}
                  $sticky={thread.sticky}
                  $seen={thread.seen}
                >
                  {thread.title}
                </Title>
              </td>

              <td className="author">
                {thread.user.fullName}
                <span>{thread.user.email}</span>
              </td>
              {isDirectMessage && (
                <td>
                  <RecipientsList title={thread.recipients?.join(', ') || ''}>
                    {formatRecipientsColumn(thread.recipients || []).map(
                      (item, index) => (
                        <div
                          key={index}
                          className={`recipient-item ${
                            item.isGroup ? 'group' : ''
                          } ${item.isMore ? 'more' : ''}`}
                        >
                          {item.text}
                        </div>
                      )
                    )}
                  </RecipientsList>
                </td>
              )}

              <td>
                <span title={formattedCreatedAt}>{timeAgo}</span>
              </td>

              <td width="1px">
                <div
                  className="post-count"
                  title={
                    thread.postCount > 99
                      ? thread.postCount.toString()
                      : undefined
                  }
                >
                  {formatPostCount(thread.postCount)}
                </div>
              </td>

              {admin && (
                <td width="1px">
                  <SeenBar
                    views={thread.numberOfViews ?? 0}
                    directNumber={thread.recipients.length}
                    isDirect={isDirectMessage}
                  />
                </td>
              )}
            </tr>
          )
        })}
      </tbody>
    </Table>
  )
}
